import { z } from 'zod';
import type { DataLayerPayload } from '../shared/data-layer.js';
import { fetchWithNetworkRetry } from './network-request.js';
import { IDEMPOTENT_WRITE_RETRY, NO_RETRY } from './network-retry-policies.js';
import {
  MAP_EVENT_PROP_ALLOWLIST,
  type AnalyticsEventProps,
  type MapAnalyticsTrack,
} from '../shared/analytics-schema.js';

const saveResponseSchema = z.object({ id: z.string().min(1), complete: z.boolean().optional(), data_version: z.string().optional() });
const completeResponseSchema = z.object({ complete: z.literal(true), data_version: z.string().regex(/^[0-9a-f]{64}$/) });

type SaveOperation = 'create' | 'update';
type SaveFailureReason = 'network_error' | 'http_error' | 'invalid_response' | 'map_limit_reached';
type Track = MapAnalyticsTrack;

interface SaveMapRequestBody { request_id?: string; account_id?: string; data: string; client_data_version?: string; mapId?: string; dataLayers?: DataLayerPayload[]; visibility?: 'public' | 'private' }

export interface SaveMapRequestOptions {
  requestId?: string;
  accountId?: string;
  mapId: string | null;
  encoded: string;
  expectedDataVersion?: string;
  dataLayers?: DataLayerPayload[];
  visibility?: 'public' | 'private';
  track: Track;
  fetchImpl?: typeof fetch;
  retryDelayMs?: number;
}

export class MapSaveError extends Error {
  readonly reason: SaveFailureReason;
  readonly operation: SaveOperation;
  readonly httpStatus?: number;
  readonly reported: boolean;
  readonly expectedOutcome?: 'plan_required';

  constructor(
    message: string,
    options: {
      reason: SaveFailureReason;
      operation: SaveOperation;
      httpStatus?: number;
      reported?: boolean;
      expectedOutcome?: 'plan_required';
      cause?: unknown;
    },
  ) {
    super(message, { cause: options.cause });
    this.name = 'MapSaveError';
    this.reason = options.reason;
    this.operation = options.operation;
    this.httpStatus = options.httpStatus;
    this.reported = options.reported ?? false;
    this.expectedOutcome = options.expectedOutcome;
  }
}

export class MapSaveConflictError extends MapSaveError {
  constructor() {
    super('The saved map has changed', { reason: 'http_error', operation: 'update', httpStatus: 409, reported: true });
    this.name = 'MapSaveConflictError';
  }
}

export function isReportedMapSaveError(cause: unknown): cause is MapSaveError {
  return cause instanceof MapSaveError && cause.reported;
}

export function isMapLimitReachedError(cause: unknown): cause is MapSaveError {
  return cause instanceof MapSaveError && cause.reason === 'map_limit_reached';
}

// Only fixed server messages may enter diagnostics. Never retain arbitrary response text.
const SAVE_ERROR_DETAILS = new Set([
  'Invalid JSON', 'Invalid map save', 'Invalid map data', 'Invalid map image', 'Invalid map content',
  'Invalid map title', 'Invalid map version', 'Invalid visibility', 'Password required', 'Password maps require Pro',
  'Pro required', 'No data or title provided',
  'Unauthorized', 'Forbidden', 'Not found', 'Internal error', 'DB not configured',
  'Map is too large', 'Map storage unavailable', 'Map could not be saved', 'Choose who can read this map',
  'invalid_map_data', 'invalid_map_image', 'invalid_map_content', 'map_too_large',
  'map_storage_unavailable', 'map_limit_reached', 'pro_required', 'data_layers_pro_required',
  'service_area_widget_subscription_required', 'data_layer_cloud_limit', 'rate_limited',
]);

async function getHttpSaveFailure(response: Response): Promise<{ reason: SaveFailureReason; detail?: string }> {
  try {
    const parsed = z.object({ error: z.string() }).safeParse(await response.clone().json());
    if (parsed.success && SAVE_ERROR_DETAILS.has(parsed.data.error)) {
      return {
        reason: response.status === 403 && parsed.data.error === 'map_limit_reached' ? 'map_limit_reached' : 'http_error',
        detail: parsed.data.error,
      };
    }
  } catch {
    // A missing or invalid error body remains an ordinary HTTP failure.
  }
  return { reason: 'http_error' };
}

function expectedSaveOutcome(status: number, detail?: string): 'plan_required' | undefined {
  const planErrors = ['Pro required', 'Password maps require Pro', 'pro_required', 'data_layers_pro_required', 'service_area_widget_subscription_required'];
  return status === 403 && planErrors.includes(detail ?? '') ? 'plan_required' : undefined;
}

function onlineState(): string {
  return 'navigator' in globalThis && navigator.onLine === false ? 'offline' : 'online';
}

function reportSaveFailure(track: Track, error: MapSaveError): MapSaveError {
  const errorDetailsBase = {
    kind: 'map_save_failed',
    reason: error.reason,
    operation: error.operation,
    error_name: error.name,
    error_message: error.message,
    expected_outcome: error.expectedOutcome,
  };
  const errorDetails = error.httpStatus
    ? { ...errorDetailsBase, http_status: String(error.httpStatus) }
    : errorDetailsBase;
  const props: AnalyticsEventProps<typeof MAP_EVENT_PROP_ALLOWLIST, 'map_save_failed'> = {
    reason: error.reason,
    operation: error.operation,
    online_state: onlineState(),
    error_blob: JSON.stringify(errorDetails),
  };
  if (error.httpStatus) props.http_status = String(error.httpStatus);
  track('map_save_failed', props);
  return new MapSaveError(error.message, {
    reason: error.reason,
    operation: error.operation,
    httpStatus: error.httpStatus,
    reported: true,
    expectedOutcome: error.expectedOutcome,
    cause: error.cause,
  });
}

export async function saveMapRequest({
  mapId,
  encoded,
  expectedDataVersion, dataLayers, visibility, requestId, accountId,
  track,
  fetchImpl,
  retryDelayMs,
}: SaveMapRequestOptions): Promise<{ id: string; url: string; complete?: boolean; data_version?: string }> {
  const operation: SaveOperation = mapId ? 'update' : 'create';
  const transport = saveTransport(mapId, dataLayers !== undefined);
  let response: Response;

  const body: SaveMapRequestBody = { request_id: requestId, account_id: accountId, data: encoded, ...(dataLayers && { dataLayers, mapId: mapId ?? undefined, visibility }) };
  if (mapId && expectedDataVersion) body.client_data_version = expectedDataVersion;
  try {
    response = await fetchWithNetworkRetry(transport.url, {
      method: transport.method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, {
      policy: transport.policy,
      retryDelayMs,
      fetchImpl,
    });
  } catch (cause) {
    const label = operation === 'update' ? 'Update' : 'Save';
    throw reportSaveFailure(track, new MapSaveError(`${label} failed: network error`, {
      reason: 'network_error',
      operation,
      cause,
    }));
  }

  if (response.status === 409) throw new MapSaveConflictError();
  if (!response.ok) {
    const label = operation === 'update' ? 'Update' : 'Save';
    const { reason, detail } = await getHttpSaveFailure(response);
    const error = new MapSaveError(`${label} failed: ${response.status}${detail ? ` (${detail})` : ''}`, {
      reason,
      operation,
      httpStatus: response.status,
      expectedOutcome: expectedSaveOutcome(response.status, detail),
    });
    // A forbidden update is an expected signal for save-as-copy fallback.
    if (operation === 'update' && response.status === 403) throw error;
    throw reportSaveFailure(track, error);
  }

  if (mapId && !dataLayers) return { id: mapId, url: `/m/${mapId}` };

  let result: unknown;
  try {
    result = await response.json();
  } catch (cause) {
    throw reportSaveFailure(track, new MapSaveError('Save failed: invalid response', {
      reason: 'invalid_response',
      operation,
      cause,
    }));
  }
  const parsed = saveResponseSchema.safeParse(result);
  const complete = completeResponseSchema.safeParse(result);
  if (!parsed.success || (dataLayers && !complete.success)) {
    throw reportSaveFailure(track, new MapSaveError('Save failed: invalid response', {
      reason: 'invalid_response',
      operation,
    }));
  }
  return { ...parsed.data, url: `/m/${parsed.data.id}` };
}

function saveTransport(mapId: string | null, complete: boolean) {
  if (complete) return { url: '/api/maps/save', method: 'POST', policy: NO_RETRY };
  if (mapId) return { url: `/api/maps/${encodeURIComponent(mapId)}`, method: 'PUT', policy: IDEMPOTENT_WRITE_RETRY };
  return { url: '/api/maps', method: 'POST', policy: NO_RETRY };
}
